import React, { useState, useEffect } from 'react'
import { Button, ButtonStyle } from '../../components/simple/button'
import { SimpleLayout } from '../../layouts/simple'
import { makeRequest } from '../../request'
import { Teacher } from '../../providers/context'
import { Select } from '../../components/form/select'
import { LabeledCheckbox } from '../../components/form/checkbox'
import { DateInput, TextInput } from '../../components/form/textInput'
import { linkTo } from '../../utils'
import { TemplateSelector } from './components/templateSelector'
import { Model } from 'survey-core'
import { CheckCircleIcon } from './components/surveyComponents'
import { defaultSurvey } from './components/prebuiltTemplates'
import { slk } from 'survey-core'

slk(
  'N2QzYTRhODAtYmI2Yi00NGE5LWEzZmMtNGQ5ODY2NzFmNTU0JmRvbWFpbnM6cmVhY2hteXRlYWNoLmNvbSxrZXljaGF0LmNvbSx0cm9waHkudGVhcmVjLmNvbSxsb2NhbGhvc3QsY29udGFjdHMud2lubm1lLmNvbTsxPTIwMjYtMDYtMDksMj0yMDI2LTA2LTA5LDQ9MjAyNi0wNi0wOQ=='
)

const SpecialWidrestPanel: React.FC<{ children: React.ReactNode; className?: string }> = ({ children, className }) => {
  return (
    <div
      className={`mx-auto mt-[5rem] flex w-[800px] flex-col items-start rounded-tl-[6px] rounded-tr-[6px] p-[2rem] shadow-special ${className}`}
    >
      {children}
    </div>
  )
}

export const CreateSurvey: React.FC<{ user: Teacher }> = ({ user }) => {
  const [surveyName, setSurveyName] = useState('')
  const [expirationDate, setExpirationDate] = useState<Date | undefined>(undefined)
  const [anonymous, setAnonymous] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null)
  const [isDefaultTemplate, setIsDefaultTemplate] = useState(false)

  // Add sender state
  const [sender, setSender] = useState<'teacher' | 'school_district'>(() => {
    // If user is admin (admin == 0), lock to teacher
    // If user is not admin (admin > 0), default to school_district
    return user.admin === 0 ? 'teacher' : 'school_district'
  })

  // Add custom link state
  const [easyLink, setEasyLink] = useState(() => {
    // Generate 4 random characters as default
    const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
    return Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
  })
  const [easyLinkAvailable, setEasyLinkAvailable] = useState<boolean | null>(null)
  const [checkingEasyLink, setCheckingEasyLink] = useState(false)

  // Function to generate smart custom link based on survey name
  const generateSmartLink = (name: string) => {
    if (!name.trim()) {
      // If name is empty, generate 4 random characters
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
      return Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join('')
    }

    const words = name.trim().toLowerCase().split(/\s+/)

    if (words.length === 1) {
      // If one word, use the whole word (alphanumeric only)
      return words[0].replace(/[^a-z0-9]/g, '')
    } else {
      // If multiple words, use first letter of each word
      return words
        .map((word) => word.charAt(0))
        .join('')
        .replace(/[^a-z0-9]/g, '')
    }
  }

  // Get district abbreviation from user's district data
  const districtAbbreviation = user.district?.abbreviation || user.district?.defaultAbbreviation || 'district'

  // Function to check custom link uniqueness
  const checkEasyLinkUniqueness = async (link: string, schoolId: number) => {
    if (!link || schoolId === -1) {
      setEasyLinkAvailable(null)
      return
    }

    setCheckingEasyLink(true)
    try {
      const response = await makeRequest<{ success: boolean; available: boolean }>('/form/checkEasyLinkUniqueness', {
        easy_link: link,
        school_id: schoolId,
      })

      if (response.success) {
        setEasyLinkAvailable(response.available)
      } else {
        setEasyLinkAvailable(null)
      }
    } catch (error) {
      console.error('Error checking custom link uniqueness:', error)
      setEasyLinkAvailable(null)
    } finally {
      setCheckingEasyLink(false)
    }
  }

  // console.log('user', user)

  useEffect(() => {
    if (defaultSurvey && defaultSurvey.length > 0) {
      handleTemplateSelected(defaultSurvey[0].json, false)
    }
  }, [])

  const combinedOptions = React.useMemo(() => {
    const options: Array<{
      value: number
      name: string
      initialOnly?: boolean
      disabled?: boolean
      districtId?: number
    }> = [{ value: -1, name: 'Select School', initialOnly: true }]

    if (user.districtAccess && user.district) {
      options.push({ value: 0, name: `${user.district.name} (District)` })

      // Add district schools with indentation
      if (user.district.schools && user.district.schools.length > 0) {
        user.district.schools.forEach((school) => {
          options.push({ value: school.id, name: `  — ${school.name}`, districtId: user.district.id })
        })
      }
    } else if (user.schools && user.schools.length > 0) {
      // Add user schools directly for non-district access users
      user.schools.forEach((school) => {
        options.push({ value: school.id, name: school.name })
      })
    }

    return options
  }, [user])

  const [schoolOption, setSchool] = React.useState(
    combinedOptions.length > 2
      ? combinedOptions[0]
      : combinedOptions[1] && !combinedOptions[1].disabled
        ? combinedOptions[1]
        : combinedOptions[0]
  )

  const perLevelOptions = React.useMemo(() => {
    const options: Array<{ value: string; name: string; initialOnly?: boolean }> = [
      { value: '', name: 'Select Level', initialOnly: true },
    ]

    // If sender is teacher, only show student and link options
    if (sender === 'teacher') {
      options.push({ value: 'student', name: 'Student' }, { value: 'link', name: 'Anyone with Link' })
    } else {
      // If sender is school/district, show all options
      options.push(
        { value: 'student', name: 'Student' },
        { value: 'recipient', name: 'Recipient' },
        { value: 'link', name: 'Anyone with Link' }
      )

      // Add household option if user is admin
      if (user.admin > 0) {
        options.push({ value: 'household', name: 'Household' })
      }
    }

    return options
  }, [user.admin, sender])

  const [perLevelOption, setPerLevelOption] = React.useState(perLevelOptions[0])

  // Reset form type when sender changes
  useEffect(() => {
    setPerLevelOption(perLevelOptions[0])
  }, [sender, perLevelOptions])

  // Reset custom link when per level option changes away from 'link'
  useEffect(() => {
    if (perLevelOption.value !== 'link') {
      // Generate new random 4 characters when not using link option
      const chars = 'abcdefghijklmnopqrstuvwxyz0123456789'
      setEasyLink(Array.from({ length: 4 }, () => chars[Math.floor(Math.random() * chars.length)]).join(''))
    }
  }, [perLevelOption.value])

  // Check uniqueness when custom link or school changes
  useEffect(() => {
    if (perLevelOption.value === 'link' && easyLink && schoolOption.value !== -1) {
      const timeoutId = setTimeout(() => {
        checkEasyLinkUniqueness(easyLink, schoolOption.value)
      }, 300) // Debounce by 300ms

      return () => clearTimeout(timeoutId)
    } else {
      setEasyLinkAvailable(null)
    }
  }, [easyLink, schoolOption.value, perLevelOption.value])

  // Update custom link when survey name changes
  React.useEffect(() => {
    if (perLevelOption.value === 'link') {
      const newLink = generateSmartLink(surveyName)
      setEasyLink(newLink)
    }
  }, [surveyName, perLevelOption.value])

  // Sender options
  const senderOptions = React.useMemo(() => {
    const options = [{ value: 'teacher', name: 'Teacher' }]

    // Only add school/district option if user is not admin (admin !== 0)
    if (user.admin !== 0) {
      options.push({ value: 'school_district', name: 'School/District' })
    }

    return options
  }, [user.admin])

  const isFormValid =
    surveyName !== '' &&
    expirationDate !== undefined &&
    perLevelOption.value !== '' &&
    schoolOption.value !== -1 &&
    (perLevelOption.value !== 'link' || (easyLink.trim() !== '' && easyLinkAvailable === true))

  // Get validation message for disabled form
  const getValidationMessage = () => {
    if (surveyName === '') return 'Please enter a form name'
    if (expirationDate === undefined) return 'Please select an expiration date'
    if (perLevelOption.value === '') return 'Please choose a form type'
    if (schoolOption.value === -1) return 'Please select a school'
    if (perLevelOption.value === 'link') {
      if (easyLink.trim() === '') return 'Please enter a custom link'
      if (easyLinkAvailable === false) return 'Custom link is already in use'
      if (easyLinkAvailable === null) return 'Checking link availability...'
    }
    return null
  }

  const handleSubmit = async () => {
    // Make sure the survey name is in the template before submission
    let finalTemplate = selectedTemplate
    if (selectedTemplate && surveyName) {
      try {
        const surveyObj = JSON.parse(selectedTemplate)
        if (surveyObj.title) {
          surveyObj.title.default = surveyName
          finalTemplate = JSON.stringify(surveyObj)
        }
      } catch (e) {
        finalTemplate = selectedTemplate
        //console.error('Failed to parse template JSON:', e)
      }
    }

    const payload = {
      name: surveyName,
      expiration_date: expirationDate ? expirationDate.toISOString().split('T')[0] : '',
      per_level: perLevelOption.value,
      school_id: schoolOption.value,
      anonymous,
      surveyJson: finalTemplate,
      sender_type: sender,
      easy_link: perLevelOption.value === 'link' ? easyLink.trim() : null,
    }

    try {
      const response = await makeRequest<{ success: boolean; id: number }>('/form/storeSurvey', payload)
      if (response.success) {
        window.location.href = `dashboard/${response.id}`
      } else {
        alert('Failed to create the form. Please try again.')
      }
    } catch (error) {
      alert('An error occurred while creating the form. Please try again.')
    }
  }

  const handleTemplateSelected = (templateJson: string, isFromUserAction = false) => {
    const isDefault = templateJson === defaultSurvey[0].json && !isFromUserAction
    setIsDefaultTemplate(isDefault)

    try {
      const surveyObj = JSON.parse(templateJson)

      if (isFromUserAction && surveyObj.title && surveyObj.title.default !== 'SurveyName') {
        setSurveyName(surveyObj.title.default)
      } else if (surveyName && surveyObj.title) {
        surveyObj.title.default = surveyName
      }

      setSelectedTemplate(JSON.stringify(surveyObj))

      if (!surveyName) {
        const model = new Model(templateJson)
        if (model.title && model.title !== 'SurveyName') {
          setSurveyName(model.title)
        }
      }
    } catch (e) {
      setSelectedTemplate(templateJson)

      if (!surveyName || isFromUserAction) {
        try {
          const model = new Model(templateJson)
          if (model.title && model.title !== 'SurveyName') {
            setSurveyName(model.title)
          }
        } catch (e) {
          if (isFromUserAction) {
            alert('Failed to extract form name from the template.')
          }
        }
      }
    }
  }

  return (
    <SimpleLayout footer={false}>
      <SpecialWidrestPanel>
        <div className="flex w-full items-center justify-between">
          <div className="text-2xl font-bold">Create a New Form</div>
          <div className="flex gap-2">
            <TemplateSelector
              creator={null}
              onTemplateSelected={(templateJson) => handleTemplateSelected(templateJson, true)}
              createMode={true}
            />
            <Button
              small
              buttonStyle={ButtonStyle.link}
              onClick={linkTo('https://reachmyteach.zendesk.com/hc/en-us', true)}
            >
              View Guide
            </Button>
          </div>
        </div>

        <div className="mb-0 w-full">
          {selectedTemplate && !isDefaultTemplate && (
            <div className="mt-3 rounded-md bg-blue-50 p-3 text-blue-700">
              <div className="flex items-center justify-between">
                <span>Template selected! Form content will be pre-populated after creation.</span>
                <Button
                  small
                  buttonStyle={ButtonStyle.link}
                  onClick={() => {
                    setSelectedTemplate(null)
                    setIsDefaultTemplate(false)

                    if (defaultSurvey && defaultSurvey.length > 0) {
                      setTimeout(() => {
                        handleTemplateSelected(defaultSurvey[0].json, false)
                      }, 0)
                    }
                  }}
                >
                  Clear Template
                </Button>
              </div>
            </div>
          )}

          {/* Survey Name */}
          <div className="mt-3">
            <div className="mb-[7px]">Form Name</div>
            <TextInput
              blue
              className="w-full"
              value={surveyName}
              onChange={(value) => {
                setSurveyName(value)
                if (selectedTemplate) {
                  try {
                    const surveyObj = JSON.parse(selectedTemplate)
                    if (surveyObj.title) {
                      surveyObj.title.default = value
                      setSelectedTemplate(JSON.stringify(surveyObj))
                    }
                  } catch (e) {
                    //console.error('Failed to parse template JSON:', e)
                  }
                }
              }}
            />
          </div>

          {/* Expiration Date */}
          <div className="mt-3">
            <div className="mb-[7px]">Expiration Date</div>
            <DateInput blue className="w-full" value={expirationDate} onChange={setExpirationDate} />
          </div>

          {/* Sender */}
          <div className="mt-3">
            <div className="mb-[7px]">Sender</div>
            <Select
              blue
              options={senderOptions}
              value={senderOptions.find((option) => option.value === sender) || senderOptions[0]}
              onChange={(option) => setSender(option.value as 'teacher' | 'school_district')}
              className="w-full"
              disabled={user.admin === 0} // Disable if user is admin (admin == 0)
            />
          </div>

          {/* Per Level */}
          <div className="mt-3">
            <div className="mb-[7px]">Choose Form Type</div>
            <div className="md:grid-cols-2 mt-2 grid grid-cols-1 gap-4">
              <div
                className={`cursor-pointer rounded-lg border p-4 transition-all ${
                  perLevelOption.value === 'student'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
                onClick={() => setPerLevelOption(perLevelOptions.find((o) => o.value === 'student')!)}
              >
                <div className="mb-1 flex items-center text-lg font-medium">
                  <span>Student</span>
                  {perLevelOption.value === 'student' && <CheckCircleIcon />}
                </div>
                <p className="text-sm text-gray-600">
                  Send the form to students or their parents. Only one response per student will be collected.
                </p>
              </div>

              {sender === 'school_district' && (
                <div
                  className={`cursor-pointer rounded-lg border p-4 transition-all ${
                    perLevelOption.value === 'recipient'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                  onClick={() => setPerLevelOption(perLevelOptions.find((o) => o.value === 'recipient')!)}
                >
                  <div className="mb-1 flex items-center text-lg font-medium">
                    <span>Recipient</span>
                    {perLevelOption.value === 'recipient' && <CheckCircleIcon />}
                  </div>
                  <p className="text-sm text-gray-600">
                    Send the form to any Student, Parent, or Staff member. One response per recipient will be collected.
                  </p>
                </div>
              )}

              <div
                className={`cursor-pointer rounded-lg border p-4 transition-all ${
                  perLevelOption.value === 'link'
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                }`}
                onClick={() => setPerLevelOption(perLevelOptions.find((o) => o.value === 'link')!)}
              >
                <div className="mb-1 flex items-center text-lg font-medium">
                  <span>Anyone with Link</span>
                  {perLevelOption.value === 'link' && <CheckCircleIcon />}
                </div>
                {perLevelOption.value === 'link' ? (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-1">
                      <span className="font-mono text-base font-medium text-blue-700">
                        https://reachmyteach.com/form/{districtAbbreviation || 'district'}/
                      </span>
                      <div className="relative flex-1">
                        <TextInput
                          blue
                          className="flex-1 font-mono text-base font-medium"
                          value={easyLink}
                          onChange={(value) => {
                            // Only allow alphanumeric characters
                            const cleanValue = value.replace(/[^a-zA-Z0-9]/g, '')
                            setEasyLink(cleanValue)
                          }}
                          placeholder="auto-generated from name"
                        />
                        {/* Validation indicator */}
                        {easyLink && schoolOption.value !== -1 && (
                          <div className="absolute right-2 top-1/2 -translate-y-1/2 transform">
                            {checkingEasyLink ? (
                              <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
                            ) : easyLinkAvailable === true ? (
                              <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : easyLinkAvailable === false ? (
                              <svg className="h-4 w-4 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                  fillRule="evenodd"
                                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                  clipRule="evenodd"
                                />
                              </svg>
                            ) : null}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="text-xs">
                      {easyLinkAvailable === false && (
                        <span className="text-red-600">
                          This custom link is already in use. Please choose a different one.
                        </span>
                      )}
                      {easyLinkAvailable === true && (
                        <span className="text-green-600">This custom link is available!</span>
                      )}
                      {(easyLinkAvailable === null || schoolOption.value === -1) && (
                        <span className="text-gray-600">
                          Customize the ending of your form's public link. Only letters and numbers are allowed.
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-600">
                    Create a public form that can be accessed by anyone who has the link.
                  </p>
                )}
              </div>

              {user.admin > 0 && sender === 'school_district' && (
                <div
                  className={`cursor-pointer rounded-lg border p-4 transition-all ${
                    perLevelOption.value === 'household'
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                  onClick={() => setPerLevelOption(perLevelOptions.find((o) => o.value === 'household')!)}
                >
                  <div className="mb-1 flex items-center text-lg font-medium">
                    <span>Household</span>
                    {perLevelOption.value === 'household' && <CheckCircleIcon />}
                  </div>
                  <p className="text-sm text-gray-600">
                    Send the form to households/families of students. One response per household will be collected.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Combined District/School dropdown */}
          {combinedOptions.length > 2 && (
            <div className="mt-3">
              <div className="mb-[7px]">{user.districtAccess ? 'District & School' : 'School'}</div>
              <Select blue options={combinedOptions} value={schoolOption} onChange={setSchool} className="w-full" />
            </div>
          )}

          {/* Anonymous Survey Checkbox */}
          <div className="mt-3">
            <LabeledCheckbox
              label="Anonymous Form"
              tooltip="Select if the form should be anonymous"
              checked={anonymous}
              onChecked={setAnonymous}
            />
          </div>

          {/* Submit Button */}
          <div className="mt-3">
            <div className="flex items-center">
              <Button logo onClick={handleSubmit} disabled={!isFormValid}>
                Create Form
              </Button>
            </div>
            {!isFormValid && <div className="mt-2 text-sm text-red-600">{getValidationMessage()}</div>}
          </div>
          {combinedOptions.length === 1 && (
            <div className="mt-3 rounded-md bg-yellow-50 p-3 text-yellow-700">
              <div className="flex items-center">
                <svg
                  className="mr-2 h-5 w-5"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>You are not part of a school nor have district access, to create a form</span>
              </div>
            </div>
          )}
        </div>
      </SpecialWidrestPanel>
    </SimpleLayout>
  )
}
